import { env } from 'configs/EnvironmentConfig'
import { z } from 'zod'
import fetch from 'auth/FetchInterceptor'
import { Validate } from './SchemaValidation'
import axios from 'axios'
import { AUTH_TOKEN } from 'constants/AuthConstant'

const IdentityService = {}

const tokenSchema = z
  .object({
    email: z.string().min(1).email(),
    password: z.string().min(8),
    // it exists but it's not yet used
    code: z.string(),
    refresh_token: z.enum(['token', 'cookie', 'session']).optional(),
  })
  .partial()
  .refine(
    ({ email, password, code }) =>
      (email !== undefined && password !== undefined) || code !== undefined,
    { message: 'Email and password or code must be filled' }
  )

IdentityService.login = function (data) {
  return fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/auth/token`,
    withCredentials: true,
    method: 'POST',
    data: Validate(tokenSchema, data),
  })
}

IdentityService.webNotifyToken = () =>
  fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/user/me/web-notify`,
    withCredentials: true,
    method: 'GET',
  })

// const refreshTokenSchema = z.object({
//   refresh_token: z.string(),
// })

IdentityService.refreshToken = function () {
  return axios.request({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/auth/refresh_token`,
    method: 'POST',
    withCredentials: true,
  })
}

const userSchema = z.object({
  name: z.string().min(1),
  username: z.string().min(1),
  email: z.string().min(1).email(),
  password: z.string().min(8),
  is_business: z.boolean().optional(),
  cpf: z
    .string()
    .regex(/[0-9]*/)
    .min(11),
  cnpj: z
    .string()
    .regex(/[0-9]*/)
    .min(14)
    .optional(),
  business_name: z.string().optional(),
  referral_id: z.string().optional(),
  date_of_birth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
})

const addressesSchema = z.object({
  city: z.string(),
  complement: z.string().optional(),
  district: z.string(),
  number: z.string().max(15),
  state: z.string(),
  street: z.string(),
  zipcode: z.string(),
})

const phonesSchema = z
  .object({
    area: z.string().max(2),
    country: z.string(),
    number: z.string().regex(/\d+/).max(9),
    phone_type_id: z.number().refine(value => [1, 2, 3, 4].includes(value)),
  })
  .superRefine(({ area, number }, content) => {
    if (area && !number) {
      content.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please specify an area',
      })
    } else if (!area && number) {
      content.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please specify a number',
      })
    }
  })
  .optional()

// const registerSchema = z.intersection(userSchema, {
//   addresses: addressesSchema.array(),
//   phones: phonesSchema.array(),
// })

IdentityService.register = function (data) {
  return fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/user`,
    method: 'POST',
    data: Validate(userSchema, data),
  })
}

const logoutSchema = z.object({
  access_token: z.string().min(1),
  refresh_token: z.string().min(1).optional(),
})

IdentityService.logout = function (data) {
  return fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/auth/logout`,
    method: 'POST',
    data: Validate(logoutSchema, data),
  })
}

const postPasswordRecoverySchema = z.object({
  email: z.string().min(1).email(),
})

IdentityService.postPasswordRecovery = function (data) {
  return fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/auth/password_recovery`,
    method: 'POST',
    data: Validate(postPasswordRecoverySchema, data),
  })
}

const putPasswordRecoverySchema = z.object({
  email: z.string().min(1).email(),
  code: z
    .string()
    .regex(/[0-9]*/)
    .min(6),
  password: z.string().min(8),
})

IdentityService.putPasswordRecovery = function (data) {
  return fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/auth/password_recovery`,
    method: 'PUT',
    data: Validate(putPasswordRecoverySchema, data),
  })
}

const postPasswordSchema = z.object({
  current_password: z.string().min(8),
  new_password: z.string().min(8),
})

IdentityService.postPassword = function (data) {
  return fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/user/me/password`,
    method: 'POST',
    data: Validate(postPasswordSchema, data),
  })
}

IdentityService.getMe = function () {
  return fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/user/me`,
    method: 'GET',
  })
}

IdentityService.updateMe = (user, phoneRaw, addressRaw) => {
  const { id: phoneId, ...phone } = phoneRaw
  const { id: addressId, ...address } = addressRaw

  const phoneData = Validate(phonesSchema, phone)
  const addressData = Validate(addressesSchema, address)
  const userData = Validate(userSchema.omit({ password: true }), user)

  const promise = Promise.all([
    fetch({
      url: `${env.IDENTITY_ENDPOINT_URL}/api/user/me/phone${
        phoneId ? `/${phoneId}` : ''
      }`,
      method: phoneId ? 'PUT' : 'POST',
      data: phoneData,
    }),
    fetch({
      url: `${env.IDENTITY_ENDPOINT_URL}/api/user/me/address${
        addressId ? `/${addressId}` : ''
      }`,
      method: addressId ? 'PUT' : 'POST',
      data: addressData,
    }),
  ]).then(() =>
    fetch({
      url: `${env.IDENTITY_ENDPOINT_URL}/api/user/me`,
      method: 'PUT',
      data: userData,
    })
  )

  return promise
}

IdentityService.resendVerify = () => {
  return fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/user/me/verify`,
    method: 'PATCH',
  })
}

IdentityService.verify = code => {
  const access_token = localStorage.getItem(AUTH_TOKEN)
  return axios.request({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/user/me/verify`,
    headers: { Authorization: `Bearer ${access_token}` },
    params: { code },
    method: 'GET',
  })
}

const cepSchema = z
  .string()
  .regex(/[0-9]*/)
  .min(8)

IdentityService.getCep = cep =>
  fetch({
    url: `${env.IDENTITY_ENDPOINT_URL}/api/cep/${Validate(cepSchema, cep)}`,
    method: 'GET',
  })

export default IdentityService
